import { ThemeEvents, CartUpdatedEvent } from '@theme/events';
import { processGifts, extractCartItemCollections } from '@theme/auto-gift-processor';

// MIMOYA : les changements de quantité du tiroir partent l'un après l'autre (clics rapides) : la
// dernière quantité demandée est toujours celle qui reste, et les cadeaux suivent.
let mimoyaCartQueue = Promise.resolve();

class CartDrawer extends HTMLElement {
  #rendering = false;
  #pendingState = null;

  constructor() {
    super();
    this.checkoutButton = null;
    this.openCartIcons = null;

    this.backdropClickHandler = this.#onBackdropClick.bind(this);
    this.checkoutButtonClickHandler = this.#handleCheckoutButtonClick.bind(this);
    this.cartUpdateHandler = this.#handleCartUpdate.bind(this);
    this.openHandler = this.open.bind(this);
    this.closeHandler = this.close.bind(this);
  }

  connectedCallback() {
    this.checkoutButton = this.querySelector('[data-ref="checkout-button"]');

    this.addEventListener('click', this.backdropClickHandler);

    this.checkoutButton.addEventListener('click', this.checkoutButtonClickHandler);

    document.addEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    document.addEventListener(ThemeEvents.cartOpen, this.openHandler);
    document.addEventListener(ThemeEvents.cartClose, this.closeHandler);

    // MIMOYA : panier déjà rempli à l'arrivée sur une page (règles de cadeaux changées depuis, par ex.
    // ancien bon −50 % remplacé par la crème SPF 50) : les cadeaux sont remis à jour une fois, sans
    // attendre un clic dans le panier.
    if (customElements.get('auto-gift') || document.querySelector('auto-gift')) {
      customElements.whenDefined('auto-gift').then(() => setTimeout(() => this.#syncGiftsOnLoad(), 300));
    }

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.#onSectionUnload);
      if (window.theme?.isCartDrawerOpen) {
        this.open();
        window.theme.isCartDrawerOpen = false; // Reset the flag
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartClose, this.closeHandler);
    document.removeEventListener(ThemeEvents.cartOpen, this.openHandler);

    document.removeEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    this.checkoutButton.removeEventListener('click', this.checkoutButtonClickHandler);

    this.removeEventListener('click', this.backdropClickHandler);

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:unload', this.#onSectionUnload);
    }
  }

  async #syncGiftsOnLoad() {
    if (this.#rendering) return;
    let state = null;
    try {
      state = JSON.parse(this.querySelector('[data-mimoya-cart-state]')?.textContent || 'null');
    } catch (error) {
      state = null;
    }
    if (!state || !Array.isArray(state.items) || state.items.length === 0) return;

    const sectionId = this.dataset.sectionId;
    const result = await processGifts(sectionId).catch(() => null);
    if (result?.sections?.[sectionId]) this.#applyState({ sections: result.sections, itemCount: result.itemCount });
  }

  #onSectionUnload = (event) => {
    if (event.detail.sectionId !== this.dataset.sectionId) return;

    window.theme = window.theme || {};
    window.theme.isCartDrawerOpen = this.isOpen();
  };

  #handleCheckoutButtonClick(event) {
    event.target.classList.add('is-loading');
  }

  #handleCartUpdate(event) {
    this.renderCartContents(event.detail.data);
  }

  #onBackdropClick(event) {
    if (event.target.closest('dialog[open]')) return;

    const rect = this.querySelector('.cart-drawer__inner').getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      this.close();
    }
  }

  open() {
    // Nettoyer les classes pour éviter les conflits
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    // Ajouter la classe de fermeture pour déclencher l'animation
    this.classList.add('is-closing');

    // Attendre la fin de l'animation avant de masquer complètement
    setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125); // Durée légèrement inférieure à --animation-speed pour éviter les décalages
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  #updateAutoGiftsMapping(autoGiftsHtml) {
    if (!autoGiftsHtml) return;

    const parsed = new DOMParser().parseFromString(autoGiftsHtml, 'text/html');
    const newScript = parsed.querySelector('[data-ref="cart-item-collections"]');
    const currentScript = document.querySelector('[data-ref="cart-item-collections"]');
    if (newScript && currentScript) currentScript.textContent = newScript.textContent;
  }

  async renderCartContents(parsedState) {
    // MIMOYA : une mise à jour qui arrive pendant la vérification des cadeaux n'est plus perdue :
    // la dernière reçue est rendue juste après.
    if (this.#rendering) {
      this.#pendingState = parsedState;
      return;
    }
    this.#rendering = true;

    try {
      const sectionId = this.dataset.sectionId;

      // MIMOYA : le contenu reçu avec l'ajout au panier est affiché tout de suite (le tiroir ne
      // s'ouvre plus vide). La vérification des cadeaux vient ensuite et le tiroir n'est redessiné
      // que si elle a modifié le panier.
      this.#applyState(parsedState);

      const newAutoGiftsHtml = parsedState.sections?.['auto-gifts'];
      const newAutoGifts = newAutoGiftsHtml ? new DOMParser().parseFromString(newAutoGiftsHtml, 'text/html') : null;
      const newCollections = newAutoGifts ? extractCartItemCollections(newAutoGifts) : {};

      const giftResult = await processGifts(sectionId, newCollections);

      if (giftResult?.sections?.[sectionId]) {
        this.#applyState({
          ...parsedState,
          sections: { ...parsedState.sections, ...giftResult.sections },
          itemCount: giftResult.itemCount ?? parsedState.itemCount,
        });
      }
    } finally {
      this.#rendering = false;
    }

    if (this.#pendingState) {
      const nextState = this.#pendingState;
      this.#pendingState = null;
      this.renderCartContents(nextState);
    }
  }

  #applyState(parsedState) {
    const sectionId = this.dataset.sectionId;
    const html = parsedState.sections?.[sectionId];
    if (!html) return;

    const newCartDrawer = new DOMParser().parseFromString(html, 'text/html');
    const newInner = newCartDrawer.querySelector('.cart-drawer__inner');
    if (!newInner) return;

    this.querySelector('.cart-drawer__inner').innerHTML = newInner.innerHTML;

    this.#updateAutoGiftsMapping(parsedState.sections?.['auto-gifts']);

    // MIMOYA : classe « vide » basculée dans la même tâche que le contenu (plus d'image intermédiaire
    // où le nouveau contenu resterait masqué par l'état vide).
    const isEmpty = newCartDrawer.querySelector('cart-drawer')?.classList.contains('cart-drawer--empty') ?? parsedState.itemCount == 0;
    this.classList.toggle('cart-drawer--empty', isEmpty);

    // MIMOYA : le compteur de l'icône reprend le nombre d'articles du panier rendu (même calcul que
    // Shopify au chargement de la page), et non plus le nombre ajouté par le formulaire.
    let itemCount = null;
    try {
      const state = JSON.parse(newCartDrawer.querySelector('[data-mimoya-cart-state]')?.textContent || 'null');
      if (state && Array.isArray(state.items)) {
        itemCount = state.items.reduce((total, item) => total + (Number(item.quantity) || 0), 0);
      }
    } catch (error) {
      itemCount = null;
    }
    this.#syncCartIconCount(itemCount === null ? parsedState : { ...parsedState, itemCount });
  }

  #syncCartIconCount(parsedState) {
    const cartIcon = document.querySelector('cart-icon');
    if (!cartIcon) return;

    const countEl = cartIcon.querySelector('[data-ref="cart-count"]');
    if (!countEl) return;

    if (typeof parsedState.itemCount === 'number') {
      countEl.textContent = parsedState.itemCount;
      return;
    }

    cartIcon.ensureCartCountIsCorrect();
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItem extends HTMLElement {
  constructor() {
    super();

    this.removeItemButton = null;
    this.quantitySelectorInput = null;
    this.itemRemoveHandler = this.#handleItemRemove.bind(this);
    this.quantityInputChangeHandler = this.#handleQuantityInputChange.bind(this);
  }

  connectedCallback() {
    this.removeItemButton = this.querySelector('[data-ref="remove-item"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="quantity-selector-input"]');

    this.removeItemButton?.addEventListener('click', this.itemRemoveHandler);
    this.quantitySelectorInput?.addEventListener('change', this.quantityInputChangeHandler);
  }

  disconnectedCallback() {
    this.quantitySelectorInput?.removeEventListener('change', this.quantityInputChangeHandler);
    this.removeItemButton?.removeEventListener('click', this.itemRemoveHandler);
  }

  #handleItemRemove(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.key || this.dataset.index, 0);
  }

  #handleQuantityInputChange(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.key || this.dataset.index, this.quantitySelectorInput.value);
  }

  // MIMOYA : line = clé de la ligne (data-key) : la requête vise toujours la bonne ligne, même si un
  // cadeau vient d'être ajouté et a décalé les numéros de ligne (ancien comportement : data-index).
  #updateLineItemQuantity(line, quantity) {
    this.#enableLoading();
    const target = typeof line === 'string' && line.includes(':') ? { id: line, quantity } : { line, quantity };

    const sections = [this.closest('cart-drawer').dataset.sectionId, 'auto-gifts'].join(',');
    const post = (url, payload) =>
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: `application/json` },
        body: JSON.stringify({ ...payload, sections, sections_url: window.location.pathname }),
      }).then((response) => response.text());

    // MIMOYA : retrait ou baisse de quantité en un seul appel. Les cadeaux qui ne sont plus débloqués
    // et, s'il ne reste plus rien de payant, la protection d'expédition partent dans la même requête
    // (assets/mimoya-cart-extras.js) au lieu d'être retirés ensuite un par un par le moteur de cadeaux
    // puis par mimoya-shipping-protection.js. Si Shopify refuse, requête d'origine.
    let mimoyaUpdates = null;
    try {
      mimoyaUpdates = window.mimoyaCartExtras?.getLineUpdates?.(line, quantity)?.updates || null;
    } catch (error) {
      console.error('MIMOYA cart extras:', error);
    }

    // MIMOYA : quantité augmentée : les cadeaux nouvellement débloqués sont ajoutés juste après la mise à
    // jour, avant l'affichage : le tiroir montre le nouveau prix et les cadeaux en même temps. Si l'ajout
    // échoue, le moteur de cadeaux les rajoute ensuite (comportement d'origine).
    let mimoyaAdds = [];
    try {
      mimoyaAdds = window.mimoyaCartExtras?.getLineAdds?.(line, quantity) || [];
    } catch (error) {
      console.error('MIMOYA cart extras:', error);
    }
    const parse = (text) => {
      try {
        return JSON.parse(text);
      } catch (error) {
        return null;
      }
    };

    // MIMOYA : la clé d'une ligne change quand sa remise change (passage 2 → 3 tubes…) : après un clic
    // rapide, la clé lue dans le tiroir peut être périmée. /cart/update.js retrouve quand même la ligne
    // (/cart/change la refuse) : on l'utilise pour les lignes visées par leur clé.
    const change = () => {
      if (!target.id) return post(Theme.routes.cart_change_url, target);
      return post(`${Theme.routes.cart_update_url}.js`, { updates: { [target.id]: quantity } }).then((text) => {
        const data = parse(text);
        if (data && !data.errors && !(data.status >= 400)) return text;
        return post(Theme.routes.cart_change_url, target);
      });
    };

    const run = () =>
      (mimoyaUpdates
        ? post(`${Theme.routes.cart_update_url}.js`, { updates: mimoyaUpdates }).then((text) => {
            const data = parse(text);
            if (!data || data.errors || data.status >= 400) return change();
            return text;
          })
        : change()
      ).then((text) => {
        if (mimoyaAdds.length === 0) return text;
        const data = parse(text);
        if (!data || data.errors || data.status >= 400) return text;
        return post(Theme.routes.cart_add_url, { items: mimoyaAdds })
          .then((addText) => {
            const added = parse(addText);
            return added && !added.errors && !(added.status >= 400) && added.sections ? addText : text;
          })
          .catch(() => text);
      });
    const request = (mimoyaCartQueue = mimoyaCartQueue.then(run, run));

    request
      .then((responsetext) => {
        const parsedResponseText = JSON.parse(responsetext);

        if (parsedResponseText.errors) {
          const message = String(parsedResponseText.description || parsedResponseText.message || parsedResponseText.errors);
          document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message } }));
          return;
        }

        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: parsedResponseText.item_count,
              source: 'cart-drawer-item',
              sections: parsedResponseText.sections,
            },
          ),
        );
      })
      .catch((e) => {
        console.error(e);
        this.#disableLoading();
      })
      .finally(() => {
        this.#disableLoading();
      });
  }

  #enableLoading() {
    this.classList.add('cart-drawer__item--loading');
  }

  #disableLoading() {
    this.classList.remove('cart-drawer__item--loading');
  }
}

if (!customElements.get('cart-drawer-item')) {
  customElements.define('cart-drawer-item', CartDrawerItem);
}
